import { z } from "zod";
/**
 * Colyseus's `ISeatReservation`, as returned by `matchMaker.joinOrCreate()` /
 * `matchMaker.reserveSeatFor()` and consumed by
 * `client.consumeSeatReservation(reservation)`.
 *
 * LOCKED (architecture §16 A8): the object is **flat** and travels
 * **verbatim** from the game service, through the api, to the browser. The api
 * must not reshape it, rename fields, or re-nest its contents — the client SDK
 * reads `publicAddress` / `processId` / `roomId` / `sessionId` off it exactly
 * as Colyseus wrote them, and a "tidied up" copy is how you get a WebSocket
 * pointed at the wrong process.
 *
 * The schema is therefore **loose**: unknown keys are preserved, not stripped.
 * Colyseus may add fields (0.17 already sends more than the five named here);
 * anything we drop is a join failure that only shows up at runtime.
 *
 * Consequence for `apps/api`: routes returning a reservation deliberately do
 * NOT attach a Fastify response schema, because a compiled serializer would
 * strip exactly the keys this comment exists to protect.
 */
export const seatReservationSchema = z.looseObject({
    /** Colyseus room name, e.g. "ping". */
    name: z.string().optional(),
    /** The session this reservation is for. */
    sessionId: z.string(),
    /** The room the seat is in. */
    roomId: z.string(),
    /** The Colyseus process that OWNS the room (may not be the one that reserved). */
    processId: z.string().optional(),
    /** "<public host>/<owning process port>" — no scheme, no trailing slash. */
    publicAddress: z.string().optional(),
    /**
     * Present **only** on a reservation minted by `POST /api/v1/rooms/:id/rejoin`
     * (architecture §16f A42), and it is why that route may not attach a response
     * schema either.
     *
     * Colyseus's own field: `consumeSeatReservation()` forwards it as a connection
     * option, and the server matches it against the open `allowReconnection()`
     * window and **resolves that window as reconnected**. That is A42's trap 1
     * closed by the framework rather than by our own bookkeeping — the seat, the
     * `sessionId` and the grace timer are all the ones the player already had.
     *
     * **It is not a stored credential.** A42 rejects persisting a
     * `reconnectionToken` in `localStorage`; this one is minted server-side at the
     * moment of the rejoin, only for a caller whose HttpOnly session holds a
     * `joined` participant row, travels once inside the reservation and is consumed
     * by the socket that immediately follows. Nothing writes it to storage and
     * `exposedMethods` is still `[]` — the browser never matchmakes.
     */
    reconnectionToken: z.string().optional(),
});
//# sourceMappingURL=seat-reservation.js.map